import { useState } from 'react';
import { Micro } from '../primitives';
import { Icon } from '../icons/registry';
import { useIconSystem } from '../../hooks/useIconSystem';
import styles from './ContextDrawer.module.css';

export type ContextItem = {
  label: string;
  value: string;
  muted?: boolean;
};

type ContextDrawerProps = {
  items: ContextItem[];
  /** Default copy: "What your coaches know about you". Override via prop if needed. */
  title?: string;
  /** Lead copy once opened. */
  lead?: string;
};

/**
 * Collapsible "what your coaches know" drawer. Closed by default —
 * per Claude Design feedback, the Phase 1 move was to demote this
 * from a dashboard-visible chip soup to a trust affordance at the
 * bottom. Frames as edit-your-profile prompt, not metadata dump.
 *
 * Used on mobile. Desktop surfaces this via the coach-context route
 * (not in Phase 1 scope).
 */
export function ContextDrawer({
  items,
  title = 'What your coaches know about you',
  lead = "Edit anything: your coaches use this to personalize every reply.",
}: ContextDrawerProps) {
  const iconsOn = useIconSystem();
  const [open, setOpen] = useState(false);

  return (
    <section className={styles.drawer}>
      <button
        className={styles.btn}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
      >
        <div className={styles.left}>
          <Micro as="span">Coach context</Micro>
          <span className={styles.heading}>{title}</span>
        </div>
        {/* A2 — `sys.caret` @14. ⚠️ The legacy mark SELF-COLOURS
            (`stroke="var(--ink-500)"`), which the registry forbids, so the
            wrapper carries the token and the glyph inherits it. The rotation
            and its transition move to the wrapper unchanged. */}
        {iconsOn ? (
          <span
            aria-hidden
            style={{
              display: 'inline-flex',
              color: 'var(--ink-500)',
              transform: open ? 'rotate(180deg)' : 'none',
              transition: 'transform .2s',
            }}
          >
            <Icon name="sys.caret" size={14} />
          </span>
        ) : (
        <svg
          width="14"
          height="14"
          viewBox="0 0 14 14"
          fill="none"
          stroke="var(--ink-500)"
          strokeWidth="1.8"
          style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}
          aria-hidden
        >
          <path d="M3 5l4 4 4-4" />
        </svg>
        )}
      </button>
      {open && (
        <div className={styles.body}>
          <p className={styles.lead}>{lead}</p>
          <div className={styles.grid}>
            {items.map((item) => (
              <div key={item.label} className={styles.item}>
                <div className={styles.itemLabel}>{item.label}</div>
                <div
                  className={styles.itemValue}
                  style={item.muted ? { color: 'var(--ink-400)' } : undefined}
                >
                  {item.value}
                </div>
              </div>
            ))}
          </div>
          {/* F-513 — was a dead <button> (no handler). Now a real link to the
              read-only "What Airo knows" Settings section. `<a>` (not <Link>)
              so it works regardless of Router context and natively scrolls to
              the #airo-knows anchor. */}
          <a className={styles.manage} href="/v2/settings#airo-knows">
            What Airo knows →
          </a>
        </div>
      )}
    </section>
  );
}
